import { useState } from 'react';
import { Building, Search, Trash2, Pencil, Plus, X, AlertCircle } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../api/apiClient';

interface AdminProperty {
  id: number;
  address: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  propertyType?: string;
  bedrooms?: number;
  bathrooms?: number;
  agentId?: number;
  ownerEmail?: string;
  createdAt?: string;
}

interface PropertyForm {
  address: string;
  suburb: string;
  state: string;
  postcode: string;
  type: string;
  bedrooms: string;
  bathrooms: string;
  ownerEmail: string;
  agentId: string;
  rentAmount: string;
  rentFrequency: string;
}

const EMPTY_FORM: PropertyForm = {
  address: '', suburb: '', state: '', postcode: '',
  type: '', bedrooms: '', bathrooms: '',
  ownerEmail: '', agentId: '', rentAmount: '', rentFrequency: '',
};

function PropertyModal({
  initial,
  onClose,
  onSave,
  saving,
  saveError,
}: {
  initial: AdminProperty | null;
  onClose: () => void;
  onSave: (form: PropertyForm) => void;
  saving: boolean;
  saveError: string;
}) {
  const [form, setForm] = useState<PropertyForm>(
    initial
      ? {
          address: initial.address ?? '',
          suburb: initial.suburb ?? '',
          state: initial.state ?? '',
          postcode: initial.postcode ?? '',
          type: initial.propertyType ?? '',
          bedrooms: initial.bedrooms != null ? String(initial.bedrooms) : '',
          bathrooms: initial.bathrooms != null ? String(initial.bathrooms) : '',
          ownerEmail: initial.ownerEmail ?? '',
          agentId: initial.agentId != null ? String(initial.agentId) : '',
          rentAmount: '',
          rentFrequency: '',
        }
      : EMPTY_FORM
  );

  const set = (field: keyof PropertyForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [field]: e.target.value }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {initial ? 'Edit Property' : 'New Property'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {saveError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />{saveError}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Address *</label>
            <input value={form.address} onChange={set('address')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="123 Main St" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Suburb</label>
              <input value={form.suburb} onChange={set('suburb')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Suburb" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">State</label>
              <select value={form.state} onChange={set('state')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
                <option value="">—</option>
                {['ACT','NSW','NT','QLD','SA','TAS','VIC','WA'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Postcode</label>
              <input value={form.postcode} onChange={set('postcode')} maxLength={4}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="2000" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
              <select value={form.type} onChange={set('type')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
                <option value="">—</option>
                {['house','apartment','unit','townhouse','studio','other'].map(t => (
                  <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Bedrooms</label>
              <input type="number" min={0} value={form.bedrooms} onChange={set('bedrooms')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="0" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Bathrooms</label>
              <input type="number" min={0} value={form.bathrooms} onChange={set('bathrooms')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="0" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Owner Email *</label>
            <input type="email" value={form.ownerEmail} onChange={set('ownerEmail')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="owner@example.com" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Rent Amount</label>
              <input type="number" min={0} step="0.01" value={form.rentAmount} onChange={set('rentAmount')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="500.00" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Rent Frequency</label>
              <select value={form.rentFrequency} onChange={set('rentFrequency')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
                <option value="">—</option>
                <option value="WEEKLY">Weekly</option>
                <option value="FORTNIGHTLY">Fortnightly</option>
                <option value="MONTHLY">Monthly</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-5 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button
            onClick={() => onSave(form)}
            disabled={saving}
            className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? 'Saving…' : initial ? 'Save Changes' : 'Create Property'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPropertiesPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<AdminProperty | null>(null);
  const [editTarget, setEditTarget] = useState<AdminProperty | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [actionError, setActionError] = useState('');
  const [modalError, setModalError] = useState('');

  const { data: properties = [], isLoading, isError } = useQuery<AdminProperty[]>({
    queryKey: ['admin', 'properties'],
    queryFn: () => api.get<AdminProperty[]>('/management/properties').then(r => r ?? []),
    staleTime: 30_000,
  });

  const createProperty = useMutation({
    mutationFn: (form: PropertyForm) => api.post('/management/properties', {
      address: form.address,
      suburb: form.suburb || undefined,
      state: form.state || undefined,
      postcode: form.postcode || undefined,
      type: form.type || undefined,
      bedrooms: form.bedrooms ? Number(form.bedrooms) : undefined,
      bathrooms: form.bathrooms ? Number(form.bathrooms) : undefined,
      ownerEmail: form.ownerEmail,
      agentId: form.agentId ? Number(form.agentId) : undefined,
      rentAmount: form.rentAmount ? Number(form.rentAmount) : undefined,
      rentFrequency: form.rentFrequency || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'properties'] });
      setShowCreate(false);
      setModalError('');
    },
    onError: (e: any) => setModalError(e.message ?? 'Failed to create property'),
  });

  const updateProperty = useMutation({
    mutationFn: ({ id, form }: { id: number; form: PropertyForm }) =>
      api.put(`/management/properties/${id}`, {
        address: form.address || undefined,
        suburb: form.suburb || undefined,
        state: form.state || undefined,
        postcode: form.postcode || undefined,
        type: form.type || undefined,
        bedrooms: form.bedrooms ? Number(form.bedrooms) : undefined,
        bathrooms: form.bathrooms ? Number(form.bathrooms) : undefined,
        ownerEmail: form.ownerEmail || undefined,
        agentId: form.agentId ? Number(form.agentId) : undefined,
        rentAmount: form.rentAmount ? Number(form.rentAmount) : undefined,
        rentFrequency: form.rentFrequency || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'properties'] });
      setEditTarget(null);
      setModalError('');
    },
    onError: (e: any) => setModalError(e.message ?? 'Failed to update property'),
  });

  const deleteProperty = useMutation({
    mutationFn: (id: number) => api.delete(`/management/properties/${id}`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admin', 'properties'] }); setDeleteTarget(null); },
    onError: (e: any) => setActionError(e.message ?? 'Failed to delete property'),
  });

  const filtered = properties.filter(p => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      (p.address ?? '').toLowerCase().includes(q) ||
      (p.suburb ?? '').toLowerCase().includes(q) ||
      (p.state ?? '').toLowerCase().includes(q) ||
      (p.postcode ?? '').includes(q) ||
      (p.ownerEmail ?? '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        title="Properties"
        subtitle="All properties on the platform"
        icon={<Building className="w-8 h-8" />}
      >
        <button
          onClick={() => { setModalError(''); setShowCreate(true); }}
          className="flex items-center gap-2 px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90"
        >
          <Plus className="w-4 h-4" />
          New Property
        </button>
      </PageHeader>

      {/* Search */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by address, suburb, state, postcode, or owner email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
      </div>

      {(isError || actionError) && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-red-800">{actionError || 'Failed to load properties.'}</div>
          {actionError && <button onClick={() => setActionError('')} className="ml-auto text-red-600"><X className="w-4 h-4" /></button>}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-3 border-b border-gray-200">
            <p className="text-sm text-gray-500">{filtered.length} propert{filtered.length !== 1 ? 'ies' : 'y'}</p>
          </div>
          {filtered.length === 0 ? (
            <div className="p-12 text-center text-gray-400">No properties found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beds / Baths</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Added</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filtered.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{p.address}</div>
                        <div className="text-xs text-gray-400">
                          {[p.suburb, p.state, p.postcode].filter(Boolean).join(', ')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 capitalize">{p.propertyType ?? '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {p.bedrooms != null ? `${p.bedrooms}b` : '—'} / {p.bathrooms != null ? `${p.bathrooms}ba` : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{p.ownerEmail ?? '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => { setModalError(''); setEditTarget(p); }}
                            className="p-1.5 text-gray-500 hover:text-primary hover:bg-primary/10 rounded"
                            title="Edit property"
                          ><Pencil className="w-4 h-4" /></button>
                          <button
                            onClick={() => { setActionError(''); setDeleteTarget(p); }}
                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Delete property"
                          ><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <PropertyModal
          initial={null}
          onClose={() => setShowCreate(false)}
          onSave={form => createProperty.mutate(form)}
          saving={createProperty.isPending}
          saveError={modalError}
        />
      )}

      {/* Edit modal */}
      {editTarget && (
        <PropertyModal
          initial={editTarget}
          onClose={() => setEditTarget(null)}
          onSave={form => updateProperty.mutate({ id: editTarget.id, form })}
          saving={updateProperty.isPending}
          saveError={modalError}
        />
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Property</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete <strong>{deleteTarget.address}</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button
                onClick={() => deleteProperty.mutate(deleteTarget.id)}
                disabled={deleteProperty.isPending}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteProperty.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
